// Fixed Term Contracts dashboard card (ADR-091)
// Modeled on TeamPendingActionsCard: navy header with a hero number, compact
// body, outlined-chip primary action + link. Numbers come from the ADR-091
// summary endpoint, scoped server-side (org-wide + per-Cluster breakdown for
// admins/people partners; the caller's managed hierarchy otherwise). Every
// number deep-links to the report with filters pre-applied via ftcState —
// hierarchy scopes use the ADR-092 representative filter with self.
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import { COLORS } from '../../constants/radar';
import { fixedTermContractsApi } from '../../services/api/fixedTermContractsApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { useAbility } from '../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import { EMPLOYEE_REPORTING_SCOPE } from '../../constants/employeeReportingScope';
import { getExpiryDaysColor, EXPIRY_OVERDUE } from '../../constants/fixedTermContracts';

// Same dark blue theme as Team Feedback Overview
const CARD_COLOR = '#2B436B';

// Representative days for each disjoint bucket, fed to getExpiryDaysColor so
// the card dots always match the report's row colors (≤ 30 red, 31–60 amber,
// 61–90 green).
const BUCKETS = [
  { key: 'b30', days: 30, expiresWithinDays: 30, label: 'within 30 days' },
  { key: 'b60', days: 60, expiresWithinDays: 60, label: 'in 31–60 days' },
  { key: 'b90', days: 90, expiresWithinDays: 90, label: 'in 61–90 days' },
];

const Dot = ({ color }) => (
  <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
);

const FixedTermContractsCard = () => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { user } = useAuth();
  const employee = user?.employee;

  const canView = ability.can(ACTIONS.READ, SUBJECTS.FIXED_TERM_CONTRACT_REPORT);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await fixedTermContractsApi.getFixedTermContractsSummary();
        setData(response);
      } catch (err) {
        console.error('Error fetching fixed term contracts summary:', err);
        // The card must never block the dashboard — collapse to the error state
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [canView]);

  // Rendered only for users who can read the report (ADR-090 permission)
  if (!canView) {
    return null;
  }

  // ADR-091: navigate to the report with filters pre-applied via ftcState.
  // Hierarchy scope → the ADR-092 representative filter preset with self.
  // backTo/backLabel make the report's back button return to the dashboard
  // (matching Feedback Results' "Back to Home") instead of the Reports grid;
  // they ride inside ftcState so they survive the employee-profile round trip.
  const goToReport = (extra = {}) => {
    const ftcState = { ...extra, backTo: '/', backLabel: 'Home' };
    if (data?.scope?.type === 'hierarchy' && employee?.id) {
      ftcState.representative = { id: employee.id, fullName: employee.fullName };
      ftcState.representativeScope = EMPLOYEE_REPORTING_SCOPE.HIERARCHY;
    }
    navigate('/reports/fixed-term-contracts', { state: { ftcState } });
  };

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: COLORS.BACKGROUND_LIGHT,
          width: 400,
          height: 240,
          mt: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <LoadingSpinner message="Loading contracts..." />
      </Card>
    );
  }

  const overdue = data?.overdue || 0;
  const expiring30 = data?.expiring30 || 0;
  // Summary counts are cumulative (report-header semantics) — derive the
  // disjoint buckets the card displays.
  const disjoint = {
    b30: expiring30,
    b60: (data?.expiring60 || 0) - expiring30,
    b90: (data?.expiring90 || 0) - (data?.expiring60 || 0),
  };
  const expiring90 = data?.expiring90 || 0;
  const allClear = !error && overdue === 0 && expiring90 === 0;
  const breakdownRows = data?.breakdown?.rows || [];
  const showMatrix = !error && !allClear && breakdownRows.length > 0;

  // Header hero: overdue first, then the nearest bucket, then all-clear
  const heroText = error
    ? null
    : overdue > 0
      ? <><strong>{overdue}</strong> Overdue</>
      : expiring30 > 0
        ? <><strong>{expiring30}</strong> expiring in 30 days</>
        : 'All contracts on track';

  const scopeLabel = data?.scope?.type === 'hierarchy'
    ? `Your hierarchy · ${data?.scope?.employeeCount ?? 0} ${(data?.scope?.employeeCount ?? 0) === 1 ? 'person' : 'people'}`
    : data?.scope?.label || '';

  const linkStyle = {
    color: CARD_COLOR,
    fontSize: '0.75rem',
    fontWeight: 500,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const renderActions = (primaryLabel, primaryExtra) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
      {primaryLabel && (
        <Chip
          label={primaryLabel}
          variant="outlined"
          size="small"
          onClick={() => goToReport(primaryExtra)}
          sx={{
            backgroundColor: 'transparent',
            color: CARD_COLOR,
            borderColor: CARD_COLOR,
            borderWidth: 1,
            fontWeight: 500,
            fontSize: '0.75rem',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(43, 67, 107, 0.04)' },
          }}
        />
      )}
      <Link
        to="#"
        onClick={(e) => { e.preventDefault(); goToReport(); }}
        style={linkStyle}
        onMouseOver={(e) => { e.target.style.textDecoration = 'underline'; }}
        onMouseOut={(e) => { e.target.style.textDecoration = 'none'; }}
      >
        View Report →
      </Link>
    </Box>
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: COLORS.BACKGROUND_LIGHT,
        width: showMatrix ? 440 : 400,
        mt: 1,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, backgroundColor: CARD_COLOR, color: 'white' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Fixed Term Contracts
          </Typography>
          {heroText && (
            <Typography variant="body2">
              {heroText}
              {data?.scope?.type === 'organization' ? ' · Organization-wide' : ''}
            </Typography>
          )}
        </Box>

        {error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Couldn't load summary
            </Typography>
            {renderActions(null)}
          </Box>
        ) : allClear ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No contracts expiring in the next 90 days
            </Typography>
            {scopeLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Scope: {scopeLabel}
              </Typography>
            )}
            {renderActions(null)}
          </Box>
        ) : showMatrix ? (
          <Box sx={{ p: 2 }}>
            {/* Per-Cluster matrix (org-wide scope): cluster rows × expiry buckets.
                Only clusters that actually have contracts in the window are
                rendered — a quiet cluster is dropped server-side, not shown
                as a zero row (the "Showing N of M" caption below says so). */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.7fr repeat(4, 1fr)', alignItems: 'center', rowGap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Cluster
              </Typography>
              {/* Spacing follows the app's existing convention: the relational
                  operator is spaced ("≤ 30d", as in the report header
                  "&le; 30 Days"), while numeric ranges keep a closed-up en dash
                  ("31–60d", as in the Bench Entry Lag report's "1–3 days").
                  nowrap keeps a narrow column from orphaning the "≤" or
                  breaking a range after its dash. */}
              {['Overdue', '≤ 30d', '31–60d', '61–90d'].map((h, i) => (
                <Box key={h} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  {i > 0 && <Dot color={getExpiryDaysColor([30, 60, 90][i - 1])} />}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {h}
                  </Typography>
                </Box>
              ))}

              {breakdownRows.map((row) => {
                const rowDisjoint = {
                  b30: row.expiring30,
                  b60: row.expiring60 - row.expiring30,
                  b90: row.expiring90 - row.expiring60,
                };
                const cells = [
                  { value: row.overdue, extra: { expiresWithinDays: EXPIRY_OVERDUE }, hot: true },
                  { value: rowDisjoint.b30, extra: { expiresWithinDays: 30 } },
                  { value: rowDisjoint.b60, extra: { expiresWithinDays: 60 } },
                  { value: rowDisjoint.b90, extra: { expiresWithinDays: 90 } },
                ];
                const rowScope = row.id ? { clusterId: row.id } : {};
                return (
                  <React.Fragment key={row.id || 'unassigned'}>
                    <Typography
                      variant="body2"
                      onClick={() => goToReport(rowScope)}
                      sx={{ fontWeight: 600, cursor: 'pointer', py: 0.25, '&:hover': { textDecoration: 'underline' } }}
                    >
                      {row.label}
                      {/* ADR-122: Organizational Structure Type inline after the
                          cluster name — muted monospace code, full name on
                          hover. Same one presentation as ClusterSelector /
                          BusinessUnitSelector under showStructureType; do not
                          invent a second rendering for it. */}
                      {row.orgStructureType?.code && (
                        <Typography
                          component="span"
                          title={row.orgStructureType.name}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 400,
                            color: '#6B7280',
                            fontFamily: 'monospace',
                            marginLeft: '6px',
                          }}
                        >
                          {row.orgStructureType.code}
                        </Typography>
                      )}
                    </Typography>
                    {cells.map((cell, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        onClick={() => goToReport({ ...rowScope, ...cell.extra })}
                        sx={{
                          textAlign: 'right',
                          fontWeight: cell.value > 0 ? 700 : 400,
                          color: cell.value > 0 ? (cell.hot ? '#DC2626' : 'text.primary') : 'text.disabled',
                          cursor: 'pointer',
                          py: 0.25,
                        }}
                      >
                        {cell.value > 0 ? cell.value : '—'}
                      </Typography>
                    ))}
                  </React.Fragment>
                );
              })}
            </Box>

            {data?.breakdown?.totalUnits > breakdownRows.length && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Showing {breakdownRows.length} of {data.breakdown.totalUnits} clusters · quiet ones hidden
              </Typography>
            )}

            {/* The primary action lands on exactly what it names: Overdue uses
                the dedicated predicate, not the 30-day window (which would also
                include everything expiring in the next 30 days). */}
            {renderActions(
              overdue > 0 ? 'Review Overdue' : 'Review Expiring',
              { expiresWithinDays: overdue > 0 ? EXPIRY_OVERDUE : 30 },
            )}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {overdue > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                Overdue contracts are past their end date with no processed termination or extension.
              </Typography>
            )}

            <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
              Expiring soon:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
              {BUCKETS.map((bucket) => (
                <Box
                  key={bucket.key}
                  onClick={() => goToReport({ expiresWithinDays: bucket.expiresWithinDays })}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    px: 0.5,
                    '&:hover': { backgroundColor: 'rgba(43, 67, 107, 0.06)' },
                  }}
                >
                  <Dot color={getExpiryDaysColor(bucket.days)} />
                  <Typography variant="body2" color="text.primary">
                    <strong>{disjoint[bucket.key]}</strong> {bucket.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {scopeLabel && (
              <Typography variant="caption" color="text.secondary">
                Scope: {scopeLabel}
              </Typography>
            )}

            {/* The primary action lands on exactly what it names: Overdue uses
                the dedicated predicate, not the 30-day window (which would also
                include everything expiring in the next 30 days). */}
            {renderActions(
              overdue > 0 ? 'Review Overdue' : 'Review Expiring',
              { expiresWithinDays: overdue > 0 ? EXPIRY_OVERDUE : 30 },
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FixedTermContractsCard;
